import axios from 'axios';

// Free APIs for pincode lookup
const PINCODE_APIS = {
  // Indian Postal Code API - FREE, no API key required
  INDIA_POSTAL: 'https://api.postalpincode.in/pincode/',
  
  // Alternative Indian Pincode API - FREE, no API key required
  INDIA_PINCODE_API: 'https://api.zippopotam.us/in/',
  
  // Global Postal Code API (supports multiple countries) - FREE, no API key required
  ZIPPOPOTAMUS: 'https://api.zippopotam.us/',
  
  // Additional free alternatives (backup options):
  // GeoNames API - FREE with registration (up to 1000 requests/day)
  // GEONAMES: 'http://api.geonames.org/postalCodeSearchJSON',
  
  // OpenDataSoft API - FREE, no registration
  // OPENDATA: 'https://public.opendatasoft.com/api/records/1.0/search/',
  
  // Note: All primary APIs used are completely FREE with no API keys required
};

// Country codes for different postal code formats
const COUNTRY_CODES = {
  'IN': 'in',    // India
  'US': 'us',    // United States
  'CA': 'ca',    // Canada
  'GB': 'gb',    // United Kingdom
  'AU': 'au',    // Australia
  'DE': 'de',    // Germany
  'FR': 'fr',    // France
  'IT': 'it',    // Italy
  'ES': 'es',    // Spain
  'NL': 'nl',    // Netherlands
  'BE': 'be',    // Belgium
  'CH': 'ch',    // Switzerland
  'AT': 'at',    // Austria
  'SE': 'se',    // Sweden
  'NO': 'no',    // Norway
  'DK': 'dk',    // Denmark
  'FI': 'fi',    // Finland
  'PL': 'pl',    // Poland
  'CZ': 'cz',    // Czech Republic
  'HU': 'hu',    // Hungary
  'PT': 'pt',    // Portugal
  'GR': 'gr',    // Greece
  'RO': 'ro',    // Romania
  'BG': 'bg',    // Bulgaria
  'HR': 'hr',    // Croatia
  'SK': 'sk',    // Slovakia
  'SI': 'si',    // Slovenia
  'LT': 'lt',    // Lithuania
  'LV': 'lv',    // Latvia
  'EE': 'ee',    // Estonia
  'IE': 'ie',    // Ireland
  'LU': 'lu',    // Luxembourg
  'MT': 'mt',    // Malta
  'CY': 'cy',    // Cyprus
  'MX': 'mx',    // Mexico
  'BR': 'br',    // Brazil
  'AR': 'ar',    // Argentina
  'TR': 'tr',    // Turkey
  'ZA': 'za',    // South Africa
  'JP': 'jp',    // Japan
  'KR': 'kr',    // South Korea
  'SG': 'sg',    // Singapore
  'MY': 'my',    // Malaysia
  'TH': 'th',    // Thailand
  'PH': 'ph',    // Philippines
  'ID': 'id',    // Indonesia
  'VN': 'vn',    // Vietnam
  'IN': 'in',    // India
  'CN': 'cn',    // China
  'RU': 'ru',    // Russia
};

// Validate pincode format based on country
export const validatePincodeFormat = (pincode, countryCode = 'IN') => {
  const formats = {
    'IN': /^[1-9][0-9]{5}$/, // 6 digits, first digit not 0
    'US': /^[0-9]{5}(-[0-9]{4})?$/, // 5 digits or 5+4 format
    'CA': /^[A-Za-z][0-9][A-Za-z][ -]?[0-9][A-Za-z][0-9]$/, // Canadian postal code
    'GB': /^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][A-BD-HJLNP-UW-Z]{2}$/, // UK postcode
    'DE': /^[0-9]{5}$/, // 5 digits
    'FR': /^[0-9]{5}$/, // 5 digits
    'AU': /^[0-9]{4}$/, // 4 digits
    'JP': /^[0-9]{3}-[0-9]{4}$/, // 3-4 format
  };
  
  const regex = formats[countryCode] || /^[0-9A-Za-z\s-]{3,10}$/; // Generic format
  return regex.test(pincode);
};

// Simple usage tracking for monitoring API calls
let apiUsageStats = {
  indianAPI: { calls: 0, lastReset: Date.now() },
  globalAPI: { calls: 0, lastReset: Date.now() }
};

// Reset usage stats daily
const resetUsageStats = () => {
  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;
  
  if (now - apiUsageStats.indianAPI.lastReset > oneDayMs) {
    apiUsageStats.indianAPI = { calls: 0, lastReset: now };
  }
  
  if (now - apiUsageStats.globalAPI.lastReset > oneDayMs) {
    apiUsageStats.globalAPI = { calls: 0, lastReset: now };
  }
};

// Get current usage stats
export const getUsageStats = () => {
  resetUsageStats();
  return apiUsageStats;
};

// Fetch pincode data from Indian Postal API
export const fetchIndianPincodeData = async (pincode) => {
  try {
    resetUsageStats();
    apiUsageStats.indianAPI.calls++;
    
    const response = await axios.get(`${PINCODE_APIS.INDIA_POSTAL}${pincode}`);
    
    if (response.data && response.data[0] && response.data[0].Status === 'Success') {
      const postOffices = response.data[0].PostOffice;
      if (postOffices && postOffices.length > 0) {
        const firstOffice = postOffices[0];
        
        return {
          pincode: pincode,
          city: firstOffice.District,
          state: firstOffice.State,
          country: 'India',
          area: firstOffice.Name,
          region: firstOffice.Region,
          division: firstOffice.Division,
          district: firstOffice.District,
          taluk: firstOffice.Block || firstOffice.Taluk || firstOffice.Tehsil || '', // Block is often the Taluk
          village: firstOffice.Region || firstOffice.Name || '', // Region or Post Office name can be village
          block: firstOffice.Block || '',
          allAreas: postOffices.map(po => po.Name),
          allPostOffices: postOffices.map(po => ({
            name: po.Name,
            block: po.Block,
            district: po.District,
            state: po.State,
            region: po.Region,
            division: po.Division,
            taluk: po.Block || po.Taluk || po.Tehsil || '',
            village: po.Region || po.Name || ''
          })),
          success: true
        };
      }
    }
    return { success: false, error: 'No data found for this pincode' };
  } catch (error) {
    console.error('Error fetching Indian pincode data:', error);
    return { success: false, error: 'Failed to fetch pincode data' };
  }
};

// Fetch pincode data from Zippopotamus API (supports multiple countries)
export const fetchGlobalPincodeData = async (pincode, countryCode = 'in') => {
  try {
    resetUsageStats();
    apiUsageStats.globalAPI.calls++;
    
    const code = COUNTRY_CODES[countryCode.toUpperCase()] || countryCode.toLowerCase();
    const response = await axios.get(`${PINCODE_APIS.ZIPPOPOTAMUS}${code}/${pincode}`);
    
    if (response.data) {
      const data = response.data;
      return {
        pincode: pincode,
        city: data.places && data.places[0] ? data.places[0]['place name'] : '',
        state: data.places && data.places[0] ? data.places[0].state : '',
        country: data.country,
        latitude: data.places && data.places[0] ? data.places[0].latitude : null,
        longitude: data.places && data.places[0] ? data.places[0].longitude : null,
        allPlaces: data.places || [],
        success: true
      };
    }
    return { success: false, error: 'No data found for this pincode' };
  } catch (error) {
    console.error('Error fetching global pincode data:', error);
    return { success: false, error: 'Failed to fetch pincode data' };
  }
};

// Main function to fetch pincode data
export const fetchPincodeData = async (pincode, countryCode = 'IN') => {
  // Validate pincode format first
  if (!validatePincodeFormat(pincode, countryCode)) {
    return { success: false, error: 'Invalid pincode format' };
  }

  // For Indian pincodes, try Indian API first, then fallback to global
  if (countryCode.toUpperCase() === 'IN') {
    const indianResult = await fetchIndianPincodeData(pincode);
    if (indianResult.success) {
      return indianResult;
    }
    
    // Fallback to global API
    const globalResult = await fetchGlobalPincodeData(pincode, countryCode);
    return globalResult;
  } else {
    // For other countries, use global API
    return await fetchGlobalPincodeData(pincode, countryCode);
  }
};

// Search pincodes by city name (for autocomplete suggestions)
export const searchPincodesByCity = async (cityName, countryCode = 'IN') => {
  // This would require a different API or database
  // For now, returning empty array as most free APIs don't support reverse lookup
  try {
    // You could implement city-to-pincode lookup here if you have access to such an API
    return { success: true, pincodes: [] };
  } catch (error) {
    return { success: false, error: 'Failed to search pincodes' };
  }
};

// Get common pincodes for major cities (for initial autocomplete options)
export const getCommonPincodes = () => {
  return [
    // Major Indian cities
    { pincode: '110001', city: 'New Delhi', state: 'Delhi', country: 'India', area: 'Connaught Place' },
    { pincode: '400001', city: 'Mumbai', state: 'Maharashtra', country: 'India', area: 'Fort' },
    { pincode: '560001', city: 'Bangalore', state: 'Karnataka', country: 'India', area: 'Chickpet' },
    { pincode: '600001', city: 'Chennai', state: 'Tamil Nadu', country: 'India', area: 'Fort St George' },
    { pincode: '700001', city: 'Kolkata', state: 'West Bengal', country: 'India', area: 'BBD Bagh' },
    { pincode: '500001', city: 'Hyderabad', state: 'Telangana', country: 'India', area: 'Afzal Gunj' },
    { pincode: '411001', city: 'Pune', state: 'Maharashtra', country: 'India', area: 'Pune Cantonment' },
    { pincode: '380001', city: 'Ahmedabad', state: 'Gujarat', country: 'India', area: 'Ellis Bridge' },
    { pincode: '302001', city: 'Jaipur', state: 'Rajasthan', country: 'India', area: 'Gopalbari' },
    { pincode: '226001', city: 'Lucknow', state: 'Uttar Pradesh', country: 'India', area: 'Hazratganj' },
    
    // Major global cities (examples)
    { pincode: '10001', city: 'New York', state: 'New York', country: 'United States', area: 'Manhattan' },
    { pincode: '90210', city: 'Beverly Hills', state: 'California', country: 'United States', area: 'Beverly Hills' },
    { pincode: 'SW1A 1AA', city: 'London', state: 'England', country: 'United Kingdom', area: 'Westminster' },
    { pincode: 'M5V 3A8', city: 'Toronto', state: 'Ontario', country: 'Canada', area: 'Downtown' },
    { pincode: '75001', city: 'Paris', state: 'Île-de-France', country: 'France', area: '1st arrondissement' },
    { pincode: '10115', city: 'Berlin', state: 'Berlin', country: 'Germany', area: 'Mitte' },
    { pincode: '100-0001', city: 'Tokyo', state: 'Tokyo', country: 'Japan', area: 'Chiyoda' },
    { pincode: '2000', city: 'Sydney', state: 'New South Wales', country: 'Australia', area: 'Sydney CBD' },
  ];
};

export default {
  fetchPincodeData,
  fetchIndianPincodeData,
  fetchGlobalPincodeData,
  validatePincodeFormat,
  searchPincodesByCity,
  getCommonPincodes,
  COUNTRY_CODES
};